import classNames from 'classnames'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Label } from './Label'
import { IInputWrapProps } from './models/IInputWrapProps'

import styles from './Input.module.scss'

export const InputWrap = ({
  classes,
  margin,
  label,
  value,
  wrapper = 'normal',
  disabled,
  startContent,
  endContent,
  dir,

  children,
  focused,
  customTemplates,
  onChange,
  placeholderTemplate,
}: IInputWrapProps) => {
  const rootRef = useRef<HTMLInputElement>(null)
  const endContentRef = useRef<HTMLDivElement>(null)
  const [rightSpacing, setRightSpacing] = useState<string>(`0px`)

  const valueString = useMemo(() => {
    if (typeof value === 'number' || typeof value === 'string') {
      return value + ''
    }
    return undefined
  }, [value])

  const customRootTemplate = customTemplates?.root({ children })

  useEffect(() => {
    // fix browser bug: if field has autocomplete from browser data (email, phone, etc), then
    // onChange doesn't call in base input.
    // For this case we must update value manually.
    const input = rootRef.current?.querySelector('input')
    if (input && !valueString) {
      if (input.value) {
        if (onChange) {
          onChange(input.value)
        }
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (endContent) {
      setRightSpacing(`${endContentRef.current?.clientWidth || 0}px`)
    } else {
      setRightSpacing('0px')
    }
  }, [endContent])

  useLayoutEffect(() => {
    // fix browser bug: if we change value outside to an empty string, then textarea will not clear its value
    const textarea = rootRef.current?.querySelector('textarea')
    if (!value && textarea) {
      textarea.value = ''
    }
  }, [value])

  return (
    <div
      ref={rootRef}
      className={classNames(styles.root, classes?.root, {
        [styles[`root--${wrapper}`]]: !!wrapper,
        [`margin-bottom-${margin}`]: !!margin,
        [styles['root--disabled']]: !!disabled,
        [styles[`dir-${dir}`]]: !!dir,
      })}
      data-testid={'inputRoot'}
    >
      <Label
        label={label}
        value={valueString}
        focused={focused}
        dir={dir}
        wrapper={wrapper}
        startContent={startContent}
        customTemplates={customTemplates}
        labelFieldSetClassName={classes?.fieldset}
        labelClassName={classes?.label}
        rightSpacing={rightSpacing}
      />

      {startContent && (
        <div
          className={classNames(
            styles.adornment,
            dir === 'rtl' ? styles['adornment--right'] : styles['adornment--left'],
            classes?.leftSide
          )}
          data-testid={'inputLeftSide'}
        >
          {startContent}
        </div>
      )}

      {!customRootTemplate ? (
        <div
          className={classNames(
            styles.content,
            startContent || (endContent && dir === 'rtl')
              ? styles['content--with-left-adornment']
              : '',
            endContent || (startContent && dir === 'rtl')
              ? styles['content--with-right-adornment']
              : '',
            disabled ? styles['content--disabled'] : '',
            classes?.content
          )}
          data-testid={'inputContent'}
        >
          <>
            {placeholderTemplate ? (
              <div
                className={classNames(styles.customPlaceholder, {
                  [styles['customPlaceholder--hidden']]: !!valueString,
                })}
              >
                {placeholderTemplate()}
              </div>
            ) : null}
            {children}
          </>
        </div>
      ) : (
        customRootTemplate
      )}

      {endContent && (
        <div
          ref={endContentRef}
          className={classNames(
            styles.adornment,
            dir === 'rtl' ? styles['adornment--left'] : styles['adornment--right'],
            classes?.rightSide
          )}
          data-testid={'inputRightSide'}
        >
          {endContent}
        </div>
      )}
    </div>
  )
}
